import React, { useState, useEffect, useRef } from "react"
import HomepageLogoLink from "../components/homepage-logo-link"
import { Link, withPrefix } from "gatsby"
import { slugify } from "../helpers/strings"
import menu from "../data/menu.json"
import FocusLock from "react-focus-lock"
import { useScrollDirection } from "react-use-scroll-direction"
import SubNavigation from "../components/sub-navigation"
import MobileSubNavigation from "../components/mobile-sub-navigation"

export default function SiteHeader({ path, hash }) {
  const homepageLogoLink = useRef()
  const mobileNavButton = useRef()
  const [mobileNavIsActive, setMobileNavIsActive] = useState(false)
  const [isScrollingDown, setIsScrollingDown] = useState(false)
  const { scrollDirection } = useScrollDirection()

  useEffect(() => {
    if (scrollDirection === "DOWN") {
      setIsScrollingDown(true)
    } else if (scrollDirection === "UP") {
      setIsScrollingDown(false)
    }
  }, [scrollDirection])

  const paths = path
    .split("/")
    .filter(function (el) {
      return el !== ""
    })
    .slice(0)
    .slice(-2)

  const getPageParent = () => {
    let parent
    if (
      paths[0] === "2021" ||
      !paths.length ||
      paths[1] === "message-from-the-md"
    ) {
      parent = "intro"
    } else if (paths[1] === "what-we-do") {
      parent = "what-we-do"
    } else {
      parent = paths[0]
    }
    return parent
  }

  const getPageSlug = () => {
    let slug
    if (paths[0] === "2021") {
      slug = "opening-statement"
    } else {
      slug = paths[1]
    }

    return slug
  }

  // Helper for normalizing paths for correct mobile nav active page underlining, regardless of env
  function normalizePath(path) {
    return path.replace("/external/pubs/ft/ar/2021/eng/", "/")
  }

  function toggleMobileNav() {
    const BODY_LOCK = "util__body-lock"

    if (mobileNavIsActive) {
      setMobileNavIsActive(false)
      document.body.classList.remove(BODY_LOCK)
    } else {
      setMobileNavIsActive(true)
      document.body.classList.add(BODY_LOCK)
    }
  }

  function isNavItemActive(item) {
    if (path.includes("spotlight") && item.name === "No Time to Let Up") {
      return true
    } else if (path.includes("what-we-do") && item.name === "What We Do") {
      return true
    } else if (path.includes("who-we-are") && item.name === "Who We Are") {
      return true
    } else if (path.includes("downloads") && item.name === "Downloads") {
      return true
    } else if (
      path.includes("message-from-the-md") &&
      item === "Message from the Managing Director"
    ) {
      return true
    } else {
      return false
    }
  }

  function toggleNavItemOnFocus(e) {
    // If the user is clicking, ignore this focus/blur toggle behavior and allow hover behavior to persist
    if (!document.body.classList.contains("user-is-tabbing")) {
      return
    }

    const focusClass = "site-header__nav__main__item--focus"

    let focusedItemParent = e.target.closest("li.site-header__nav__main__item")

    focusedItemParent.classList.contains(focusClass)
      ? focusedItemParent.classList.remove(focusClass)
      : focusedItemParent.classList.add(focusClass)
  }

  // Simple listener to close overlay on escape key hit
  function handleKeydown(e) {
    if (mobileNavIsActive && e.keyCode === 27) {
      toggleMobileNav()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown)
    return () => {
      document.removeEventListener("keydown", handleKeydown)
    }
  })

  return (
    <header
      className={
        isScrollingDown ? "site-header site-header--minimal" : "site-header"
      }
      id="site-header"
    >
      <div className="site-header__inner container">
        <HomepageLogoLink
          passedRef={homepageLogoLink}
          classes="site-header__logo"
          path={path}
        />
        <nav className="site-header__nav util__desktop-only">
          <ul className="site-header__nav__main">
            {/* Output letter from MD */}
            <li
              className={
                "site-header__nav__main__item" +
                (isNavItemActive("Message from the Managing Director")
                  ? " site-header__nav__main__item--active"
                  : "")
              }
            >
              <Link
                to="/message-from-the-md"
                aria-current={
                  path.includes("message-from-the-md") ? "page" : null
                }
              >
                Message from the Managing Director
              </Link>
            </li>
            {/* Loop through menu.json and output items */}
            {menu.map((item, index) => {
              let itemSlug = slugify(item.name)

              return item.hideDesktop ? null : (
                <li
                  key={"nav-item-desktop-" + itemSlug}
                  id={"nav-item-" + itemSlug}
                  className={
                    "site-header__nav__main__item" +
                    (isNavItemActive(item)
                      ? " site-header__nav__main__item--active"
                      : "")
                  }
                  aria-controls={"child-item-" + itemSlug}
                >
                  {item.href ? (
                    <Link
                      to={item.href}
                      aria-current={path.includes(item.href) ? "page" : null}
                      onFocus={toggleNavItemOnFocus}
                      onBlur={toggleNavItemOnFocus}
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      onFocus={toggleNavItemOnFocus}
                      onBlur={toggleNavItemOnFocus}
                    >
                      {item.name}
                    </button>
                  )}
                  <ul
                    id={"child-item-" + itemSlug}
                    className="site-header__nav__sub"
                  >
                    {item.children.map((child, index) => (
                      <li key={"child-item-" + child.name}>
                        {child.href.includes(".pdf") ? (
                          <a
                            href={withPrefix("/") + child.href}
                            aria-current={
                              path.includes(child.href) ? "page" : null
                            }
                            onFocus={toggleNavItemOnFocus}
                            onBlur={toggleNavItemOnFocus}
                          >
                            {child.name}
                          </a>
                        ) : (
                          <Link
                            to={child.href}
                            aria-current={
                              path.includes(child.href) ? "page" : null
                            }
                            onFocus={toggleNavItemOnFocus}
                            onBlur={toggleNavItemOnFocus}
                          >
                            {child.name}
                          </Link>
                        )}
                        {child.children ? (
                          <ul className="site-header__nav__super-sub">
                            {child.children.map((subchild, index) => (
                              <li key={"subchild-item-" + subchild.name}>
                                {subchild.href.includes(".pdf") ? (
                                  <a
                                    href={withPrefix("/") + subchild.href}
                                    aria-current={
                                      path.includes(subchild.href)
                                        ? "page"
                                        : null
                                    }
                                    onFocus={toggleNavItemOnFocus}
                                    onBlur={toggleNavItemOnFocus}
                                  >
                                    {subchild.name}
                                  </a>
                                ) : (
                                  <Link
                                    to={subchild.href}
                                    aria-current={
                                      path.includes(subchild.href)
                                        ? "page"
                                        : null
                                    }
                                    onFocus={toggleNavItemOnFocus}
                                    onBlur={toggleNavItemOnFocus}
                                  >
                                    {subchild.name}
                                  </Link>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </li>
              )
            })}
          </ul>
        </nav>
        <FocusLock
          disabled={mobileNavIsActive ? false : true}
          shards={
            mobileNavIsActive ? [homepageLogoLink, mobileNavButton] : null
          }
          returnFocus={true}
          noFocusGuards={true}
        >
          <nav
            id="site-mobile-nav"
            className="site-mobile-nav util__mobile-only"
            aria-hidden={mobileNavIsActive ? "false" : "true"}
            tabIndex="-1"
          >
            <ul className="site-mobile-nav__main">
              {menu.map((item, index) => {
                return (
                  <li key={"nav-item-" + item.name}>
                    {item.href ? (
                      <Link
                        className={
                          normalizePath(path + hash) ===
                            normalizePath(item.href) &&
                          (path.includes("spotlight") ||
                            path.includes("who-we-are") ||
                            normalizePath(path + hash) === "/")
                            ? "no-color-change"
                            : null
                        }
                        to={item.href}
                        aria-current={
                          normalizePath(path + hash) ===
                          normalizePath(item.href)
                            ? "page"
                            : null
                        }
                        onClick={toggleMobileNav}
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <div>{item.name}</div>
                    )}
                    <ul>
                      {item.children.map((child, index) => (
                        <li key={"child-item-" + child.name}>
                          {child.href.includes(".pdf") ? (
                            <a
                              href={withPrefix("/") + child.href}
                              onClick={toggleMobileNav}
                            >
                              {child.name}
                            </a>
                          ) : (
                            <Link
                              to={child.href}
                              aria-current={
                                normalizePath(path + hash) ===
                                normalizePath(child.href)
                                  ? "page"
                                  : null
                              }
                              onClick={toggleMobileNav}
                            >
                              {child.name}
                            </Link>
                          )}
                          {child.children ? (
                            <ul className="site-mobile-nav__main__super-sub">
                              {child.children.map((subchild, index) => (
                                <li key={"subchild-item-" + subchild.name}>
                                  {subchild.href.includes(".pdf") ? (
                                    <a
                                      href={withPrefix("/") + subchild.href}
                                      onClick={toggleMobileNav}
                                    >
                                      {subchild.name}
                                    </a>
                                  ) : (
                                    <Link
                                      to={subchild.href}
                                      aria-current={
                                        normalizePath(path + hash) ===
                                        normalizePath(subchild.href)
                                          ? "page"
                                          : null
                                      }
                                      onClick={toggleMobileNav}
                                    >
                                      {subchild.name}
                                    </Link>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </li>
                )
              })}
            </ul>
          </nav>
        </FocusLock>
        <button
          className="site-nav__menu-button util__mobile-only"
          type="button"
          aria-label="Toggle mobile navigation"
          aria-owns="site-mobile-nav"
          onClick={toggleMobileNav}
          ref={mobileNavButton}
        >
          <span
            className={
              "site-nav__menu-button__icon" +
              (mobileNavIsActive ? " site-nav__menu-button__icon--active" : "")
            }
          ></span>
        </button>
      </div>
      {getPageParent() !== "intro" &&
      getPageParent() !== "downloads" &&
      getPageParent() !== "message-from-the-md" &&
      getPageParent() !== "eng" ? (
        <>
          <SubNavigation id={getPageParent()} current={getPageSlug()} />
          <MobileSubNavigation id={getPageParent()} current={getPageSlug()} />
        </>
      ) : null}
    </header>
  )
}
